import { useState } from 'react';
import type { StepProps } from '../../OBWWizard';
import { submitPowApplication, PowApiError } from '../../../lib/pow-api';

// Phase 5c.5 (ADR-030 D3) — submission step. Submitted-info summary + Submit
// button. POST /portal/pow/submit; on success transitions to the Status step via
// onNext.
//
// ⛔ NO BOT CHECK HERE, ON PURPOSE (Jeff, 2 October 2026 — ADR-029 §5a amendment):
// "a bot check belongs at the door, not inside the house." This step is only
// reachable inside a signed-in session, and the session is the proof — customer
// sign-in is itself bot-checked. A second check here added a failure point (a
// Cloudflare outage stopped partner applications) and no protection. Abuse stays
// bounded by the one-active-application rule (ADR-029 §5b) and the suitability
// review. Do not re-add a widget to this step.

interface SubmittedData {
  preFillToken?: string;
  customerNumber?: string;
  email?: string;
  businessName?: string;
  websiteUrl?: string;
  jurisdiction?: 'AU' | 'NZ' | 'US';
  registrationNumber?: string;
  descriptionOfPractice?: string;
  // ADR-077 A1.4 — set by the entry fork's "Start fresh" path; supersedes the
  // in-progress Brief at submit instead of hitting APPLICATION_IN_PROGRESS.
  supersedeApplicationId?: string;
}

export function PartnerSubmitStep({ data, onNext }: StepProps) {
  const d = data as SubmittedData;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  async function handleSubmit() {
    if (!d.preFillToken || !d.businessName || !d.websiteUrl || !d.jurisdiction ||
        !d.registrationNumber || !d.descriptionOfPractice || !d.customerNumber) {
      setError({ code: 'INCOMPLETE', message: 'Some application details are missing. Go back to confirm.' });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitPowApplication({
        preFillToken: d.preFillToken,
        businessName: d.businessName,
        websiteUrl: d.websiteUrl,
        registrationNumber: d.registrationNumber,
        jurisdiction: d.jurisdiction,
        descriptionOfPractice: d.descriptionOfPractice,
        customerNumber: d.customerNumber,
        ...(d.supersedeApplicationId ? { supersedeApplicationId: d.supersedeApplicationId } : {}),
      });
      onNext({ applicationId: result.applicationId, submittedAt: result.submittedAt, status: result.status });
    } catch (err) {
      if (err instanceof PowApiError) {
        setError({ code: err.code, message: err.message });
      } else {
        setError({ code: 'NETWORK', message: 'Could not submit. Check your connection and try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Submit your application</h2>
      <p className="obw-subtitle">
        Review the summary below. When you submit, we’ll run the suitability assessment right
        away and show you the outcome here — it usually takes under a minute.
      </p>

      <div className="obw-auth-box">
        <p className="obw-form-section" style={{ marginTop: 0 }}>Application summary</p>
        <dl style={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: '8px', columnGap: '16px', margin: 0 }}>
          <dt style={{ color: 'var(--text-dim)' }}>Customer number</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{d.customerNumber}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Email</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{d.email}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Business name</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{d.businessName}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Website</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{d.websiteUrl}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Jurisdiction</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{d.jurisdiction}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Registration</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{d.registrationNumber}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Description</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{d.descriptionOfPractice}</dd>
        </dl>
      </div>

      {error && (
        <div className="portal-wizard-error" role="alert" style={{ marginTop: '16px' }}>
          <strong>{error.code}</strong> · {error.message}
        </div>
      )}

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={() => void handleSubmit()}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit application →'}
        </button>
      </div>
    </div>
  );
}
